import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { dateTime, money, num } from "../lib/format";
import { Button, DataState, Notice, StatusBadge } from "../components/ui";
import type { Invoice } from "../lib/types";

type ReceiptInvoice = Invoice & {
  billToSnapshot: Record<string, string | null> | null;
  vehicleSnapshot: Record<string, string | number | null> | null;
  location: { name: string; addressLine1: string | null; city: string | null; state: string | null; postalCode: string | null; phone: string | null };
  allocations: Array<{
    id: string;
    amount: number;
    payment: { method: string; provider: string; receivedAt: string; externalId: string | null };
  }>;
  depositApplications: Array<{ id: string; amount: number; createdAt: string }>;
  creditNotes: Array<{ id: string; number: string; amount: number; reason: string }>;
};

/**
 * A printable receipt rendered from the invoice SNAPSHOT.
 *
 * Customer and vehicle details come from what was frozen at issuance, not from
 * the live records, so reprinting an old receipt shows what was actually
 * agreed (build guide / 04 step 6).
 */
export function ReceiptPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();

  const invoice = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => api.get<ReceiptInvoice>(`/invoices/${invoiceId}`),
    enabled: Boolean(invoiceId),
  });

  const inv = invoice.data;
  const balance = inv ? inv.total - inv.amountPaid - inv.amountCredited : 0;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between print:hidden">
        <h1 className="text-xl font-semibold text-ink">Receipt</h1>
        <Button variant="secondary" onClick={() => window.print()}>
          Print
        </Button>
      </div>

      <DataState loading={invoice.isLoading} error={invoice.error}>
        {inv && (
          <article className="rounded-lg border border-slate-200 bg-white p-8 print:border-0 print:p-0">
            <header className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <h2 className="text-lg font-semibold text-ink">{inv.location.name}</h2>
                <p className="text-sm text-slate-600">
                  {[inv.location.addressLine1, inv.location.city, inv.location.state, inv.location.postalCode]
                    .filter(Boolean)
                    .join(", ")}
                </p>
                {inv.location.phone && (
                  <p className="text-sm text-slate-600">{inv.location.phone}</p>
                )}
              </div>
              <div className="text-right">
                <p className="font-mono text-lg font-semibold">{inv.number}</p>
                <p className="text-sm text-slate-500">Issued {dateTime(inv.issuedAt)}</p>
                <div className="mt-1">
                  <StatusBadge status={inv.status} />
                </div>
              </div>
            </header>

            <div className="mb-6 grid gap-6 sm:grid-cols-2">
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Billed to
                </h3>
                {inv.billToSnapshot ? (
                  <address className="text-sm not-italic text-ink">
                    {inv.billToSnapshot.companyName && (
                      <>
                        {inv.billToSnapshot.companyName}
                        <br />
                      </>
                    )}
                    {inv.billToSnapshot.name}
                    <br />
                    {inv.billToSnapshot.addressLine1 && (
                      <>
                        {inv.billToSnapshot.addressLine1}
                        <br />
                      </>
                    )}
                    {[inv.billToSnapshot.city, inv.billToSnapshot.state, inv.billToSnapshot.postalCode]
                      .filter(Boolean)
                      .join(", ")}
                    <br />
                    {inv.billToSnapshot.phone}
                  </address>
                ) : (
                  <p className="text-sm text-slate-500">
                    Not frozen yet. This invoice has not been issued.
                  </p>
                )}
              </div>

              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Vehicle
                </h3>
                {inv.vehicleSnapshot ? (
                  <p className="text-sm text-ink">
                    {[inv.vehicleSnapshot.year, inv.vehicleSnapshot.make, inv.vehicleSnapshot.model]
                      .filter(Boolean)
                      .join(" ")}
                    <br />
                    <span className="font-mono text-xs text-slate-600">
                      VIN {inv.vehicleSnapshot.vin ?? "—"}
                    </span>
                    <br />
                    <span className="text-slate-600">
                      Plate {inv.vehicleSnapshot.plate ?? "—"}
                      {inv.vehicleSnapshot.mileageIn
                        ? ` · ${Number(inv.vehicleSnapshot.mileageIn).toLocaleString()} mi`
                        : ""}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">No vehicle recorded.</p>
                )}
              </div>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-300 text-left">
                  <th className="pb-2 font-semibold">Description</th>
                  <th className="pb-2 text-right font-semibold">Qty</th>
                  <th className="pb-2 text-right font-semibold">Unit</th>
                  <th className="pb-2 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {inv.lines?.map((l) => (
                  <tr key={l.id}>
                    <td className="py-2">
                      {l.description}
                      <span className="ml-2 text-xs uppercase text-slate-400">{l.lineType}</span>
                    </td>
                    <td className="py-2 text-right tabular-nums">{num(l.quantity)}</td>
                    <td className="py-2 text-right tabular-nums">{money(l.unitPrice)}</td>
                    <td className="py-2 text-right tabular-nums">{money(l.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <dl className="ml-auto mt-4 max-w-xs space-y-1 border-t border-slate-300 pt-3 text-sm">
              <Line label="Subtotal" value={money(inv.subtotal)} />
              {inv.discountTotal > 0 && (
                <Line label="Discounts" value={`-${money(inv.discountTotal)}`} />
              )}
              <Line label="Sales tax" value={money(inv.taxTotal)} />
              <Line label="Total" value={money(inv.total)} strong />
            </dl>

            {(inv.allocations.length > 0 || inv.depositApplications.length > 0) && (
              <section className="mt-6 border-t border-slate-200 pt-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Payments received
                </h3>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-100">
                    {inv.allocations.map((a) => (
                      <tr key={a.id}>
                        <td className="py-1.5 text-slate-600">
                          {dateTime(a.payment.receivedAt)} ·{" "}
                          {a.payment.method.toLowerCase().replace("_", " ")}
                          {a.payment.provider !== "manual" && (
                            <span className="ml-2 font-mono text-xs text-slate-400">
                              {a.payment.externalId}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">{money(a.amount)}</td>
                      </tr>
                    ))}
                    {inv.depositApplications.map((d) => (
                      <tr key={d.id}>
                        <td className="py-1.5 text-slate-600">
                          {dateTime(d.createdAt)} · deposit applied
                        </td>
                        <td className="py-1.5 text-right tabular-nums">{money(d.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {inv.creditNotes.length > 0 && (
              <section className="mt-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Credits
                </h3>
                {inv.creditNotes.map((c) => (
                  <p key={c.id} className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      <span className="font-mono text-xs">{c.number}</span> · {c.reason}
                    </span>
                    <span className="tabular-nums">-{money(c.amount)}</span>
                  </p>
                ))}
              </section>
            )}

            <div className="mt-6 flex justify-between border-t-2 border-ink pt-3 text-base font-semibold">
              <span>{balance <= 0 ? "Paid in full" : "Balance due"}</span>
              <span className="tabular-nums">{money(Math.max(balance, 0))}</span>
            </div>

            {inv.status === "DRAFT" && (
              <div className="mt-4 print:hidden">
                <Notice tone="warn">
                  This invoice is still a draft. It has no number, no snapshot and is not a valid
                  receipt until it is issued.
                </Notice>
              </div>
            )}

            <footer className="mt-8 border-t border-slate-200 pt-4 text-xs text-slate-500">
              <p>
                Thank you for your business. Keep this receipt for your records. Questions about
                this invoice should go to {inv.location.phone ?? "the shop"}.
              </p>
            </footer>
          </article>
        )}
      </DataState>
    </div>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between ${strong ? "font-semibold text-ink" : "text-slate-600"}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
